import { observer } from "mobx-react";
import { StandardFormInput } from "modules/common/components/form/StandardFormInput";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import React from "react";
import { Schema } from "yup";
import { fields, WorkloadPlanModes } from "../models/settings";
import styles from "./WorkloadPlanSettings.module.scss";

interface ModeOption {
    id: string;
    label: string;
    description: string;
}

const OPTIONS: ModeOption[] = [
    {
        id: WorkloadPlanModes.timesheetDriven,
        label: "Табель влияет на план работ",
        description:
            "Каждый день план сверяется с фактическим табелем. Невыполненная за день работа переезжает вправо: " +
            "продлевает ближайший будущий отрезок того же проекта, а если такого нет — встаёт на следующий рабочий " +
            "день, сдвигая остальные работы. Сделанное досрочно списывается с будущих дней, и всё, что за ним, " +
            "подтягивается влево. Выполненный по плану день остаётся на месте. На странице плана доступна кнопка " +
            "«Сверить с табелем».",
    },
    {
        id: WorkloadPlanModes.dailyShift,
        label: "Каждый день — новый план работ",
        description:
            "Табель на план не влияет, прошедшие дни не учитываются. Каждый новый день весь график целиком " +
            "сдвигается вправо на один рабочий день, поэтому работы всегда начинаются с сегодняшнего дня. " +
            "Выполненное убирается из плана вручную.",
    },
    {
        id: WorkloadPlanModes.fixed,
        label: "Фиксированный план работ",
        description:
            "С планом не делается ничего: он остаётся ровно таким, каким его составили, а дни постепенно уходят " +
            "в прошлое. Все изменения — только вручную.",
    },
];

/**
 * Поведение планового табеля со временем: одно на всю компанию.
 * Варианты взаимоисключающие, поэтому переключатель, а не набор флажков.
 */
const WorkloadPlanSettingsBase: React.FC<WorkloadPlanSettingsProps> = ({ schema }) => (
    <div className="columns">
        <StandardFormInput name={fields.workloadPlanMode} schema={schema} small={true} inline={false} label="">
            {({ field, form }) => (
                <div className={styles.options}>
                    {OPTIONS.map((option) => {
                        const selected = (field.value || WorkloadPlanModes.timesheetDriven) === option.id;

                        return (
                            <div
                                key={option.id}
                                className={`${styles.option} ${selected ? styles.selected : ""}`}
                                onClick={() => {
                                    form.setFieldValue(field.name, option.id);
                                    form.setFieldTouched(field.name, true);
                                }}
                            >
                                <PlanrButton
                                    type="neutral"
                                    icon={selected ? "general-checkbox" : "general-zero-checkbox"}
                                    size="small"
                                />

                                <div className={styles.text}>
                                    <div className={styles.label}>{option.label}</div>
                                    <div className={styles.description}>{option.description}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </StandardFormInput>
    </div>
);

interface WorkloadPlanSettingsProps {
    schema: Schema<any>;
}

export const WorkloadPlanSettings = observer(WorkloadPlanSettingsBase);
